import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MapPin, Map, LocateFixed, Building2, Home, Trees, Store, Sparkles, Castle } from 'lucide-react';
import { PlaceSearchInput } from '@/components/PlaceSearchInput';
import { useLocationCtx } from '@/contexts/LocationContext';

const TABS = [
  { id: 'buy', label: 'Buy', params: { listing_type: 'buy' } },
  { id: 'rent', label: 'Rent', params: { listing_type: 'rent' } },
  { id: 'commercial', label: 'Commercial', params: { property_type: 'commercial' } },
];

const BUDGETS = {
  buy: [[0, 5000000, 'Under ₹50 Lac'], [5000000, 10000000, '₹50 Lac – 1 Cr'], [10000000, 20000000, '₹1 – 2 Cr'], [20000000, 50000000, '₹2 – 5 Cr'], [50000000, null, '₹5 Cr+']],
  rent: [[0, 20000, 'Under ₹20K'], [20000, 50000, '₹20K – 50K'], [50000, 100000, '₹50K – 1 Lac'], [100000, null, '₹1 Lac+']],
};

const CHIPS = [
  { id: 'current-location', label: 'Near me', icon: LocateFixed, action: 'locate' },
  { id: 'apartment', label: 'Apartment', icon: Building2, params: { property_type: 'apartment' } },
  { id: 'house', label: 'Independent House', icon: Home, params: { property_type: 'house' } },
  { id: 'villa', label: 'Villa', icon: Castle, params: { property_type: 'villa' } },
  { id: 'plot', label: 'Plot', icon: Trees, params: { property_type: 'plot' } },
  { id: 'commercial', label: 'Commercial', icon: Store, params: { property_type: 'commercial' } },
  { id: 'new-projects', label: 'New Projects', icon: Sparkles, params: { is_project: 'true' } },
];

export const HeroSearch = () => {
  const navigate = useNavigate();
  const { location, detect } = useLocationCtx();
  const placeRef = useRef(null);
  const [tab, setTab] = useState('buy');
  const [query, setQuery] = useState('');
  const [ptype, setPtype] = useState('any');
  const [budget, setBudget] = useState('any');


  const buildParams = (extra = {}) => {
    const p = new URLSearchParams();
    Object.entries({ ...TABS.find((t) => t.id === tab).params, ...extra }).forEach(([k, v]) => v && p.set(k, v));
    if (ptype !== 'any' && !extra.property_type && tab !== 'commercial') p.set('property_type', ptype);
    if (budget !== 'any') {
      const [min, max] = budget.split('-');
      if (min && min !== '0') p.set('min_price', min);
      if (max && max !== 'null') p.set('max_price', max);
    }
    const place = placeRef.current;
    if (place && (place.city || place.locality)) {
      if (place.city) p.set('city', place.city);
      if (place.locality) p.set('locality', place.locality);
      else if (place.name && place.name !== place.city) p.set('locality', place.name);
    } else if (query.trim()) {
      p.set('q', query.trim());
    } else if (location?.city) {
      p.set('city', location.city);
    }
    return p;
  };

  const go = (path = '/properties', extra = {}) => navigate(`${path}?${buildParams(extra).toString()}`);
  const budgetOptions = BUDGETS[tab === 'rent' ? 'rent' : 'buy'];

  return (
    <section className="relative min-h-[88vh] flex items-center" data-testid="hero-search-section">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2200&q=80" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-[#F9F9F7]" />
      </div>

      <div className="relative container-luxury pt-28 pb-20 w-full">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
          <p className="text-white/80 text-sm font-medium tracking-wide mb-4 inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#D4AF37]" /> {location?.city ? `Showing homes near ${location.city}` : 'Verified homes across 24+ Indian cities'}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white mb-8">
            Find a Property<br className="hidden sm:block" /> That Fits Your Life
          </h1>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="max-w-4xl">
          <div className="inline-flex bg-white/15 backdrop-blur-md rounded-t-xl p-1 gap-1">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => { setTab(t.id); setBudget('any'); }} data-testid={`hero-tab-${t.id}`}
                className={`px-5 py-2 text-sm font-semibold rounded-lg transition-colors ${tab === t.id ? 'bg-white text-[#0F172A] shadow' : 'text-white hover:bg-white/10'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-b-2xl rounded-tr-2xl shadow-2xl p-3 md:p-3.5 flex flex-col md:flex-row gap-2">
            <PlaceSearchInput value={query} onChange={(v) => { setQuery(v); placeRef.current = null; }} onEnter={() => go()}
              onSelect={(d) => { placeRef.current = d; }}
              placeholder="Search city, locality, landmark or project"
              inputClassName="w-full h-14 pl-12 pr-4 rounded-xl bg-slate-50 border border-slate-200 text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A]/20 focus:border-[#0F172A] text-base"
              testid="hero-search-input" />
            {tab !== 'commercial' && (
              <Select value={ptype} onValueChange={setPtype}>
                <SelectTrigger className="h-14 md:w-44 rounded-xl bg-slate-50 border-slate-200" data-testid="hero-type-select"><SelectValue placeholder="Property type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All types</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="house">Independent House</SelectItem>
                  <SelectItem value="villa">Villa</SelectItem>
                  <SelectItem value="plot">Plot</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={budget} onValueChange={setBudget}>
              <SelectTrigger className="h-14 md:w-44 rounded-xl bg-slate-50 border-slate-200" data-testid="hero-budget-select"><SelectValue placeholder="Budget" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any budget</SelectItem>
                {budgetOptions.map(([min, max, label]) => <SelectItem key={label} value={`${min}-${max}`}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => go()} className="h-14 px-7 rounded-xl bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold text-base" data-testid="hero-search-submit-button">
              <Search className="h-5 w-5 mr-2" /> Search
            </Button>
            <Button onClick={() => go('/map-search')} variant="outline" className="h-14 px-5 rounded-xl border-slate-300 text-[#0F172A] font-semibold hover:bg-slate-50" data-testid="hero-explore-map-button">
              <Map className="h-5 w-5 mr-2" /> Map
            </Button>
          </div>

          <div className="flex gap-2 mt-4 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {CHIPS.map((c) => (
              <button key={c.id} data-testid={`hero-chip-${c.id}`}
                onClick={() => (c.action === 'locate' ? (detect(), go('/map-search')) : go('/properties', c.params))}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-sm font-medium hover:bg-white hover:text-[#0F172A] transition-colors">
                <c.icon className="h-4 w-4" /> {c.label}
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
